import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Award, ClipboardList, Star, AlertOctagon, Plane,
  TrendingUp, Clock, ChevronRight, CheckCircle2, Trophy,
  Send, FileEdit, FileText, Download,
} from "lucide-react";

/**
 * Phase 5L — Employee Portal Appraisal page
 * Shows: contribution snapshot · mistakes · appreciations · pending self-appraisal
 */
export default function PortalAppraisal() {
  const [settings, setSettings] = useState(null);
  const [contribution, setContribution] = useState(null);
  const [pending, setPending] = useState(null);
  const [activeCycle, setActiveCycle] = useState(null);
  const [letters, setLetters] = useState([]);
  const [self, setSelf] = useState({});
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [sett, contrib, pend, allMine] = await Promise.all([
        api.get("/appraisal/settings"),
        api.get("/appraisal/my-contribution"),
        api.get("/appraisal/my-pending"),
        api.get("/appraisal/cycles"),
      ]);
      setSettings(sett.data);
      setContribution(contrib.data);
      setPending(pend.data);
      // Letters: cycles in LETTER_ISSUED or ACCEPTED
      setLetters((allMine.data || []).filter(c => c.status === "LETTER_ISSUED" || c.status === "ACCEPTED"));
      // Pick first DRAFT cycle as the actionable one
      const draft = (pend.data.as_employee || [])[0];
      if (draft) {
        const { data: full } = await api.get(`/appraisal/cycles/${draft.id}`);
        setActiveCycle(full);
        setSelf(full.self_appraisal || {});
        const ansMap = {};
        (full.answers || []).forEach(a => { ansMap[a.question_id] = a; });
        setAnswers(ansMap);
      }
    } catch (e) {
      toast.error("Failed to load appraisal data");
    }
  };
  useEffect(() => { load(); }, []);

  const setAns = (qid, patch) => setAnswers({ ...answers, [qid]: { ...(answers[qid] || { question_id: qid }), ...patch } });

  const save = async (submit) => {
    if (!activeCycle) return;
    setSaving(true);
    try {
      await api.put(`/appraisal/cycles/${activeCycle.id}/self-appraisal`, {
        answers: Object.values(answers).map(a => ({
          question_id: a.question_id,
          employee_rating: a.employee_rating ?? null,
          employee_comment: a.employee_comment || "",
        })),
        key_achievements: self.key_achievements || "",
        challenges_faced: self.challenges_faced || "",
        training_needs: self.training_needs || "",
        submit,
      });
      toast.success(submit ? "Submitted to HOD ✅" : "Saved as draft");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const ackMistake = async (id) => {
    try {
      await api.post(`/appraisal/mistakes/${id}/acknowledge`);
      toast.success("Acknowledged");
      const r = await api.get("/appraisal/my-contribution");
      setContribution(r.data);
    } catch (e) {
      toast.error("Failed");
    }
  };

  const downloadLetter = async (cyc) => {
    try {
      const resp = await api.get(`/appraisal/cycles/${cyc.id}/letter`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `AppraisalLetter_${cyc.employee_code}_${(cyc.cycle_name || "").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const ackLetter = async (cyc) => {
    if (!window.confirm("Acknowledge receipt of this appraisal letter? This action is final.")) return;
    try {
      await api.post(`/appraisal/cycles/${cyc.id}/acknowledge`);
      toast.success("Letter acknowledged ✓");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  // If admin hasn't turned ON the appraisal toggle, show empty-state
  if (settings && !settings.active) {
    return (
      <div className="max-w-2xl mx-auto bg-amber-50 border border-amber-200 rounded-lg p-6 text-center" data-testid="appraisal-disabled">
        <Award className="h-10 w-10 text-amber-500 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-amber-900 mb-1">Appraisal Cycle Not Active</h2>
        <p className="text-sm text-amber-700">Your HR will enable the appraisal cycle when it's time. Meanwhile, your contribution metrics keep tracking in the background.</p>
        {contribution && <ContributionPanel contrib={contribution.contribution} />}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="portal-appraisal">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
          <Award className="h-3 w-3" /> My Appraisal · {settings?.cycle_name || "Current Cycle"}
        </div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>Performance Appraisal</h1>
      </div>

      {/* Contribution snapshot — always visible */}
      {contribution && <ContributionPanel contrib={contribution.contribution} />}

      {/* Appraisal Letters — issued / accepted */}
      {letters.length > 0 && (
        <div className="bg-white border border-violet-200 rounded-lg p-5 space-y-3" data-testid="my-letters">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-violet-700" />
            <h2 className="text-base font-bold text-slate-900">Appraisal Letters</h2>
          </div>
          <p className="text-xs text-slate-600">Your appraisal has been approved. Download your letter and acknowledge to formally accept the revised terms.</p>
          <div className="space-y-2">
            {letters.map(c => (
              <div key={c.id} className="border border-violet-100 bg-violet-50/30 rounded p-3 flex flex-wrap items-center gap-3 justify-between" data-testid={`letter-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-slate-900">{c.cycle_name}</div>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-600">
                    <span>Grade: <b>{c.mgt_approval?.grade || "—"}</b></span>
                    <span>Hike: <b className="text-emerald-700">{c.mgt_approval?.final_hike_pct ?? "—"}%</b></span>
                    {c.status === "ACCEPTED" && (
                      <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded text-[10px] font-semibold">ACKNOWLEDGED ✓</span>
                    )}
                    {c.status === "LETTER_ISSUED" && (
                      <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-[10px] font-semibold">AWAITING ACK</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => downloadLetter(c)} className="h-7 text-[11px]" data-testid={`download-letter-${c.id}`}>
                    <Download className="h-3 w-3 mr-1" />Download PDF
                  </Button>
                  {c.status === "LETTER_ISSUED" && (
                    <Button size="sm" onClick={() => ackLetter(c)} className="h-7 text-[11px] brand-bg brand-bg-hover text-white" data-testid={`ack-letter-${c.id}`}>
                      <CheckCircle2 className="h-3 w-3 mr-1" />Acknowledge
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Self appraisal form */}
      {activeCycle ? (
        <div className="bg-white border border-red-200 rounded-lg p-5 space-y-4" data-testid="self-form">
          <div className="flex items-center gap-2">
            <FileEdit className="h-4 w-4 text-red-700" />
            <h2 className="text-base font-bold">Self Appraisal — {activeCycle.cycle_name}</h2>
          </div>
          <p className="text-xs text-slate-600">Rate yourself on each parameter (1–5), share key achievements and challenges. Submit when ready — your HOD will review next.</p>

          <div className="space-y-3">
            {groupBy(activeCycle.questions, "category").map(([cat, qs]) => (
              <div key={cat} className="border border-slate-200 rounded p-3 bg-slate-50/40">
                <h3 className="text-[11px] uppercase tracking-wider font-bold text-slate-700 mb-2">{cat}</h3>
                {qs.map((q) => (
                  <div key={q.id} className="grid grid-cols-12 gap-2 items-start py-1.5 border-t border-slate-100 first:border-t-0">
                    <div className="col-span-6 text-xs text-slate-800">{q.text}</div>
                    <div className="col-span-2">
                      <RatingPicker value={answers[q.id]?.employee_rating} onChange={(v) => setAns(q.id, { employee_rating: v })} testid={`rate-${q.id}`} />
                    </div>
                    <div className="col-span-4">
                      <input type="text" value={answers[q.id]?.employee_comment || ""}
                              onChange={(e) => setAns(q.id, { employee_comment: e.target.value })}
                              placeholder="Comment (optional)"
                              className="w-full text-xs border border-slate-300 rounded px-2 py-1 h-7"
                              data-testid={`comment-${q.id}`} />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <NarrativeField label="Key Achievements" value={self.key_achievements} onChange={(v) => setSelf({ ...self, key_achievements: v })} testid="key-achievements" />
          <NarrativeField label="Challenges Faced" value={self.challenges_faced} onChange={(v) => setSelf({ ...self, challenges_faced: v })} testid="challenges" />
          <NarrativeField label="Training Needs" value={self.training_needs} onChange={(v) => setSelf({ ...self, training_needs: v })} testid="training" />

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
            <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="save-draft">Save Draft</Button>
            <Button onClick={() => save(true)} disabled={saving} className="brand-bg brand-bg-hover text-white" data-testid="submit-hod">
              <Send className="h-3.5 w-3.5 mr-1" />{saving ? "Submitting…" : "Submit to HOD"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-emerald-50 border border-emerald-200 rounded p-4 text-sm text-emerald-900" data-testid="no-draft">
          <CheckCircle2 className="h-4 w-4 inline mr-1.5" />
          No pending self-appraisal. Your previous submission is being reviewed by HOD / Management.
        </div>
      )}

      {/* Mistakes I need to acknowledge */}
      {contribution?.contribution?.mistakes?.recent?.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-lg p-4" data-testid="my-mistakes">
          <h2 className="text-sm font-bold mb-2 flex items-center gap-1.5"><AlertOctagon className="h-4 w-4 text-amber-600" />Mistake Log ({contribution.contribution.mistakes.recent.length})</h2>
          <div className="space-y-2">
            {contribution.contribution.mistakes.recent.map(m => (
              <div key={m.id} className="border border-slate-200 rounded p-2.5" data-testid={`mistake-${m.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${m.severity === "critical" ? "bg-red-100 text-red-700" : m.severity === "major" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"}`}>{m.severity}</span>
                      <span className="text-[11px] text-slate-500">{m.occurred_on}</span>
                    </div>
                    <p className="text-xs text-slate-800 mt-1">{m.description}</p>
                  </div>
                  {!m.acknowledged && (
                    <Button size="sm" variant="outline" onClick={() => ackMistake(m.id)} className="text-[11px] h-7" data-testid={`ack-${m.id}`}>Acknowledge</Button>
                  )}
                  {m.acknowledged && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">ACK ✓</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Appreciation Wall */}
      {contribution?.contribution?.appreciations?.recent?.length > 0 && (
        <div className="bg-white border border-emerald-200 rounded-lg p-4" data-testid="my-appreciations">
          <h2 className="text-sm font-bold mb-2 flex items-center gap-1.5"><Trophy className="h-4 w-4 text-emerald-600" />Appreciation Wall</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {contribution.contribution.appreciations.recent.map(a => (
              <div key={a.id} className="border border-emerald-100 bg-emerald-50/50 rounded p-2.5">
                <div className="flex items-center gap-1.5">
                  <Star className="h-3.5 w-3.5 text-amber-500" />
                  <span className="text-xs font-bold text-slate-800">{a.title}</span>
                  <span className="ml-auto text-[10px] text-slate-500">{a.occurred_on}</span>
                </div>
                {a.description && <p className="text-xs text-slate-600 mt-1">{a.description}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- helpers ----------
function groupBy(arr, key) {
  const m = {};
  (arr || []).forEach(it => { m[it[key]] = m[it[key]] || []; m[it[key]].push(it); });
  return Object.entries(m).sort();
}

function NarrativeField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-slate-600">{label}</Label>
      <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className="text-xs" data-testid={testid} />
    </div>
  );
}

function RatingPicker({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-0.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} onClick={() => onChange(n)} className="p-0.5 hover:scale-110 transition-transform">
          <Star className={`h-4 w-4 ${(value || 0) >= n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
        </button>
      ))}
    </div>
  );
}

function ContributionPanel({ contrib }) {
  if (!contrib) return null;
  return (
    <div className="bg-gradient-to-br from-red-50 via-white to-amber-50 border border-red-200 rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="contribution-panel">
      <Tile color="emerald" icon={<ClipboardList className="h-4 w-4" />}
              label="Delegation Tasks"
              value={`${contrib.delegation.completed}/${contrib.delegation.total}`}
              hint={`${contrib.delegation.completion_pct}% done · ${contrib.delegation.overdue} overdue`} />
      <Tile color="blue" icon={<ClipboardList className="h-4 w-4" />}
              label="Checklist Tasks"
              value={`${contrib.checklist.completed}/${contrib.checklist.total}`}
              hint={`${contrib.checklist.completion_pct}% completion`} />
      <Tile color="indigo" icon={<TrendingUp className="h-4 w-4" />}
              label="Attendance"
              value={`${contrib.attendance.attendance_pct}%`}
              hint={`${contrib.attendance.present_days}/${contrib.attendance.working_days} days · ${contrib.attendance.late_count} late`} />
      <Tile color="amber" icon={<Plane className="h-4 w-4" />}
              label="Leaves Taken"
              value={`${Object.values(contrib.leaves).reduce((a, b) => a + b, 0)} d`}
              hint={`CL ${contrib.leaves.CL} · EL ${contrib.leaves.EL} · PL ${contrib.leaves.PL}`} />
      <Tile color="red" icon={<AlertOctagon className="h-4 w-4" />}
              label="Mistakes Logged"
              value={contrib.mistakes.total}
              hint={`Cr ${contrib.mistakes.summary.critical} · Mj ${contrib.mistakes.summary.major} · Mn ${contrib.mistakes.summary.minor}`} />
      <Tile color="emerald" icon={<Trophy className="h-4 w-4" />}
              label="Appreciations"
              value={contrib.appreciations.total}
              hint="kudos received" />
      <Tile color="slate" icon={<Clock className="h-4 w-4" />}
              label="Period"
              value="12 mo"
              hint={`${contrib.period.from} → ${contrib.period.to}`} />
    </div>
  );
}

function Tile({ color, icon, label, value, hint }) {
  const colors = {
    emerald: "border-emerald-200 bg-emerald-50/60 text-emerald-700",
    blue: "border-blue-200 bg-blue-50/60 text-blue-700",
    indigo: "border-indigo-200 bg-indigo-50/60 text-indigo-700",
    amber: "border-amber-200 bg-amber-50/60 text-amber-700",
    red: "border-red-200 bg-red-50/60 text-red-700",
    slate: "border-slate-200 bg-slate-50/60 text-slate-700",
  };
  return (
    <div className={`border rounded p-2.5 ${colors[color]}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold">{icon}{label}</div>
      <div className="text-xl font-bold font-mono mt-0.5 text-slate-900">{value}</div>
      <div className="text-[10px] text-slate-500 mt-0.5 truncate">{hint}</div>
    </div>
  );
}
